import { useEffect, useState, type ReactNode } from "react";
import { alpha, Btn, Card, Dropdown, Icon, IconBtn, Pill, Toggle, T } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { Screen, PageHeader } from "../../shell/chrome";
import { useAutosave, AutosaveBadge } from "../../shell/editor-hooks";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { TicketsSubTabs } from "./TicketsSubtabs";
import { eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "../events/eventSectionCounts";
import { TicketTypePicker } from "./TicketTypePicker";
import { useTicketTypes } from "./ticket-types-store";
import {
  TicketGroupStore, useTicketGroups, tgNewGroup, tgSyncTiersFromTypes, tgTier, tgUid, tgStep, tgInput, tgMoney,
  tgFlowCount, tgKindPill, TG_PALETTE, TG_STEP_TYPES, TG_TIMINGS,
  type TicketGroup, type TicketTier, type FlowStep, type FlowStepType,
} from "./ticket-groups-store";
import type { TicketType, TicketKind } from "./ticket-types-store";
import { useT, t as i18nT } from "../../lib/i18n";

const iconByName = (name: string) => (Icon as Record<string, (p?: { size?: number; style?: React.CSSProperties }) => ReactNode>)[name] || Icon.mail;

function TgField({ label, hint, children, htmlFor }: { label?: string; hint?: string; children: ReactNode; htmlFor?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {label ? <label htmlFor={htmlFor} style={{ fontSize: 13, fontWeight: 500, color: T.n1 }}>{label}</label> : null}
      {children}
      {hint ? <div style={{ fontSize: 12, color: T.soft }}>{hint}</div> : null}
    </div>
  );
}

export default function TicketGroupEditorPage() {
  const t = useT();
  const nav = useNav();
  const isNew = !!(nav.params && nav.params.new);
  const viewOnly = !!(nav.params && nav.params.view);
  const id = nav.params && (nav.params.id as string | undefined);

  const [form, setForm] = useState<TicketGroup>(() => {
    if (isNew) return tgNewGroup();
    const g = (id && TicketGroupStore.get(id)) || null;
    return g ? tgSyncTiersFromTypes(JSON.parse(JSON.stringify(g))) : tgNewGroup();
  });
  const [openTier, setOpenTier] = useState<string | null>(form.tiers[0] ? form.tiers[0].id : null);
  const [pickingType, setPickingType] = useState(false);
  const canWrite = !viewOnly && can("events.write");

  const routeKey = `${isNew ? "new" : (id || "")}:${viewOnly ? "v" : "e"}`;
  useEffect(() => {
    const next = isNew ? tgNewGroup() : tgSyncTiersFromTypes((id && JSON.parse(JSON.stringify(TicketGroupStore.get(id) || tgNewGroup()))) || tgNewGroup());
    setForm(next);
    setOpenTier(next.tiers[0] ? next.tiers[0].id : null);
  }, [routeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTier = (tid: string, patch: Partial<TicketTier>) => setForm((f) => ({ ...f, tiers: f.tiers.map((t) => t.id === tid ? { ...t, ...patch } : t) }));
  const addTypeAsTier = (type: TicketType) => {
    const t = tgTier({ name: type.name, kind: type.kind, price: type.price || 0, color: type.color, desc: type.desc || "", defaultAllocation: type.defaultAllocation || 50, typeId: type.id });
    setForm((f) => ({ ...f, tiers: [...f.tiers, t] }));
    setOpenTier(t.id);
  };
  const removeTier = (tid: string) => setForm((f) => ({ ...f, tiers: f.tiers.filter((t) => t.id !== tid) }));
  const dupTier = (tid: string) => setForm((f) => {
    const src = f.tiers.find((t) => t.id === tid); if (!src) return f;
    const copy: TicketTier = JSON.parse(JSON.stringify(src));
    copy.id = tgUid("tier"); copy.name = src.name + " (copy)"; copy.flow.forEach((s) => (s.id = tgUid(s.type)));
    const i = f.tiers.findIndex((t) => t.id === tid);
    const next = [...f.tiers]; next.splice(i + 1, 0, copy);
    return { ...f, tiers: next };
  });

  const steps = form.tiers.reduce((s, t) => s + tgFlowCount(t), 0);
  const saveDisabled = !form.name.trim() || form.tiers.length === 0 || form.tiers.some((t) => !t.name.trim());
  const autoStatus = useAutosave({ enabled: !viewOnly && !saveDisabled, data: form, save: () => { const { _new, ...clean } = form; void _new; TicketGroupStore.upsert(clean); } });

  const onSave = async () => {
    const { _new, ...clean } = form; void _new;
    try {
      await TicketGroupStore.upsert(clean);
      toast({ tone: "success", title: isNew ? "Ticket group created" : "Ticket group saved", subtitle: t("tickets.groupEditorPage.cleanNameUntitledGroupCleanTiersLengthTierCleanTiersLength1.icu", { name: clean.name || t("tickets.groupEditorPage.untitledGroup", "Untitled group"), count: clean.tiers.length }) });
      nav.go("ticket-groups");
    } catch (e) {
      console.warn("ticket-group save failed:", e);
      toast({ tone: "danger", title: t("tickets.groupEditorPage.couldnTSaveTicketGroup", "Couldn't save ticket group"), subtitle: t("tickets.groupEditorPage.nothingWasSavedYourEditsAreStillHereRetryInAMoment", "Nothing was saved — your edits are still here. Retry in a moment.") });
    }
  };

  return (
    <Screen rail="events" tabs={eventsSectionTabs(nav, "tickets", useEventSectionCounts())} activeTab="tickets">
      <TicketsSubTabs nav={nav} active="groups" typesCount={useTicketTypes().length} groupsCount={useTicketGroups().length} />
      <PageHeader
        title={isNew ? t("tickets.groupsLibrary.newTicketGroup", "New ticket group") : (viewOnly ? form.name || t("tickets.ticketGroupEditorPage.ticketGroup", "Ticket group") : t("tickets.ticketGroupEditorPage.editFormNameTicketGroup", { value0: form.name || t("tickets.ticketGroupEditorPage.ticketGroup", "Ticket group") }))}
        subtitle={t("tickets.groupEditorPage.formTiersLengthTierFormTiersLength1SStepsCommsStep.icu", { tierCount: form.tiers.length, stepCount: steps })}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("ticket-groups")}><Icon.arrow dir="left" size={11} /> {t("tickets.groupEditorPage.ticketGroups", "Ticket groups")}</span>}
        actions={viewOnly ? (
          <Btn icon={<Icon.edit size={14} />} onClick={() => nav.go("ticket-groups.edit", { id: form.id })}>{t("tickets.groupEditorPage.editGroup", "Edit group")}</Btn>
        ) : (<>
          <AutosaveBadge status={autoStatus} />
          <Btn variant="secondary" onClick={() => nav.go("ticket-groups")}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn icon={<Icon.check size={14} />} disabled={saveDisabled} onClick={onSave}>{isNew ? t("tickets.ticketGroupEditorPage.createGroup", "Create group") : t("account.details.saveChanges", "Save changes")}</Btn>
        </>)}
      />

      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(420px, 1fr) minmax(340px, 420px)", minHeight: 0, borderTop: `1px solid ${T.border}`, marginTop: 16 }}>
        <div style={{ borderRight: `1px solid ${T.border}`, background: T.canvas, overflow: "auto", padding: "20px 24px 40px" }}>
          <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
            <TgField label={t("tickets.groupEditorPage.groupName", "Group name")} htmlFor="tg-name">
              <input id="tg-name" value={form.name} disabled={!canWrite} autoFocus={isNew} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t("tickets.groupEditorPage.eGStandardEventPaidConference", "e.g. Standard event, Paid conference")} style={tgInput()} />
            </TgField>
            <TgField label={t("tickets.groupEditorPage.description", "Description")} hint={t('tickets.groupEditorPage.hint')}>
              <input value={form.description} disabled={!canWrite} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("tickets.groupEditorPage.shortSummaryOfWhenToUseThisGroup", "Short summary of when to use this group")} style={tgInput()} />
            </TgField>
          </Card>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("tickets.groupEditorPage.ticketTiers", "Ticket tiers")}</div>
              <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{t("tickets.groupEditorPage.builtFromYourTicketTypesEachTierKeepsItsOwnCommsFlow", "Built from your ticket types — each tier keeps its own comms flow.")}</div>
            </div>
            {canWrite ? <Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPickingType(true)}>{t("tickets.groupEditorPage.addTicketType", "Add ticket type")}</Btn> : null}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {form.tiers.map((t, i) => (
              <TierEditor key={t.id} tier={t} index={i} total={form.tiers.length} open={openTier === t.id} canWrite={canWrite}
                onToggle={() => setOpenTier((o) => (o === t.id ? null : t.id))}
                onChange={(patch) => setTier(t.id, patch)}
                onDuplicate={() => dupTier(t.id)}
                onRemove={() => { removeTier(t.id); if (openTier === t.id) setOpenTier(null); }} />
            ))}
            {form.tiers.length === 0 ? (
              <div style={{ padding: 28, border: `1px dashed ${T.n4}`, borderRadius: 8, textAlign: "center", color: T.soft, background: T.white }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{t("tickets.groupEditorPage.noTiersYet", "No tiers yet")}</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>{t("tickets.groupEditorPage.addATicketTypeToBuildThisGroup", "Add a ticket type to build this group.")}</div>
                {canWrite ? <div style={{ marginTop: 12 }}><Btn size="sm" icon={<Icon.plus size={13} />} onClick={() => setPickingType(true)}>{t("tickets.groupEditorPage.addTicketType", "Add ticket type")}</Btn></div> : null}
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ background: T.canvas, overflow: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Banner first, never last (operator rule, 2026-07-30) — the allocation
              caveat is what the tier counts below have to be read against. */}
          <div style={{ padding: 14, background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8, display: "flex", gap: 10, alignItems: "flex-start", flexShrink: 0 }}>
            <span style={{ width: 24, height: 24, borderRadius: 6, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.info size={12} /></span>
            <div style={{ flex: 1, fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>{t("tickets.groupEditorPage.editingAGroupUpdatesItEverywhereItSUsedEachEventKeeps", "Editing a group updates it everywhere it's used. Each event keeps its own")}{" "}<b>{t("tickets.groupEditorPage.allocation", "allocation")}</b> {t("tickets.groupEditorPage.perTierSoChangingCountsHereNeverOverridesALiveEvent", "per tier, so changing counts here never overrides a live event.")}</div>
          </div>
          <Card style={{ padding: 0, overflow: "hidden", flexShrink: 0 }}>
            <div style={{ height: 6, display: "flex" }}>{form.tiers.map((t) => <span key={t.id} style={{ flex: 1, background: t.color }} />)}</div>
            <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.n1, letterSpacing: -0.2 }}>{form.name || "Untitled group"}</div>
                <p style={{ fontSize: 13, color: T.soft, margin: "4px 0 0", lineHeight: 1.5 }}>{form.description || "No description yet."}</p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {form.tiers.map((tr) => {
                  const pill = tgKindPill(tr.kind);
                  return (
                    <div key={tr.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: T.n6, borderRadius: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 999, background: tr.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tr.name || "Untitled tier"}</span>
                      <Pill tone={pill.tone}>{pill.label}</Pill>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.n1, minWidth: 52, textAlign: "right" }}>{(tr.kind === "free" || tr.kind === "invite") ? t('tickets.groupEditorPage.text') : tgMoney(tr.price)}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 12, color: T.soft, flexWrap: "wrap" }}>
                <span><Icon.ticket size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{form.tiers.length} {t("tickets.groupEditorPage.tier", "tier")}{" "}{form.tiers.length === 1 ? "" : t('tickets.groupEditorPage.text2')}</span>
                <span><Icon.send size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{steps} {t("tickets.groupEditorPage.commsStep", "comms step")}{" "}{steps === 1 ? "" : t('tickets.groupEditorPage.text2')}</span>
                <span><Icon.calendar size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{form.usedBy || 0} {t("tickets.groupEditorPage.event", "event")}{" "}{form.usedBy === 1 ? "" : t('tickets.groupEditorPage.text2')}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
      {pickingType ? <TicketTypePicker exclude={form.tiers.map((t) => t.typeId).filter(Boolean) as string[]} onPick={addTypeAsTier} onClose={() => setPickingType(false)} /> : null}
    </Screen>
  );
}

function TierEditor({ tier, index, total, open, onToggle, onChange, onDuplicate, onRemove, canWrite }: {
  tier: TicketTier; index: number; total: number; open: boolean; canWrite: boolean;
  onToggle: () => void; onChange: (patch: Partial<TicketTier>) => void; onDuplicate: () => void; onRemove: () => void;
}) {
  const t = useT();
  const nav = useNav();
  const locked = !!tier.typeId;
  const pill = tgKindPill(tier.kind);
  const setKind = (kind: TicketKind) => { const price = (kind === "free" || kind === "invite") ? 0 : (Number(tier.price) || 50); onChange({ kind, price }); };
  return (
    <Card style={{ padding: 0, overflow: "hidden", border: `1px solid ${open ? T.p2 : T.border}` }}>
      <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer", background: open ? T.p4 : T.white }}>
        <span style={{ width: 4, height: 34, borderRadius: 999, background: tier.color, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{tier.name || `Tier ${index + 1}`}</span>
            <Pill tone={pill.tone}>{pill.label}</Pill>
          </div>
          <div style={{ fontSize: 12, color: T.soft, marginTop: 2, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, color: T.n1 }}>{(tier.kind === "free" || tier.kind === "invite") ? t('tickets.groupEditorPage.text') : tgMoney(tier.price)}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon.send size={11} /> {tgFlowCount(tier)}{t("tickets.groupEditorPage.stepCommsFlow", "-step comms flow")}</span>
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", gap: 2 }}>
          {canWrite ? <IconBtn size={32} label={t("tickets.groupEditorPage.duplicateTier", "Duplicate tier")} onClick={onDuplicate}><Icon.copy size={13} /></IconBtn> : null}
          {canWrite && total > 1 ? <IconBtn size={32} label={t("tickets.groupEditorPage.removeTier", "Remove tier")} onClick={onRemove}><Icon.trash size={13} /></IconBtn> : null}
        </div>
        <span style={{ display: "inline-flex", transform: open ? "rotate(180deg)" : "none", transition: "transform 140ms", color: T.soft }}><Icon.chev size={14} /></span>
      </div>

      {open ? (
        <div style={{ padding: "16px 18px 18px", borderTop: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 16 }}>
          {locked ? (
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: 14, background: T.n6, border: `1px solid ${T.border}`, borderRadius: 8 }}>
              <span style={{ width: 4, alignSelf: "stretch", borderRadius: 999, background: tier.color, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{tier.name || "Untitled type"}</span>
                  <Pill tone={pill.tone}>{pill.label}</Pill>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{(tier.kind === "free" || tier.kind === "invite") ? t('tickets.groupEditorPage.text') : tgMoney(tier.price)}</span>
                </div>
                {tier.desc ? <div style={{ fontSize: 12.5, color: T.soft, marginTop: 4 }}>{tier.desc}</div> : null}
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 12, color: T.soft, flexWrap: "wrap" }}>
                  <Icon.lock size={12} /> {i18nT("tickets.groupEditorPage.namePriceAccessAmpColourAreManagedInTicketTypes.rendered", "Name, price, access & colour are managed in Ticket Types.")}
                  {canWrite ? <span onClick={() => nav.go("ticket-types.edit", { id: tier.typeId })} style={{ color: T.p1, fontWeight: 600, cursor: "pointer" }}>{t("tickets.groupEditorPage.editType", "Edit type →")}</span> : null}
                </div>
              </div>
            </div>
          ) : (<>
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", gap: 12 }}>
              <TgField label={t("tickets.groupEditorPage.tierName", "Tier name")}><input value={tier.name} disabled={!canWrite} onChange={(e) => onChange({ name: e.target.value })} placeholder={t("tickets.groupEditorPage.eGVip", "e.g. VIP")} style={tgInput()} /></TgField>
              <TgField label={t("tickets.groupEditorPage.type", "Type")}>
                <Dropdown
                  options={[
                    { value: "free", label: t("tickets.groupEditorPage.free", "Free") },
                    { value: "paid", label: t("tickets.groupEditorPage.paid", "Paid") },
                    { value: "invite", label: t("tickets.groupEditorPage.inviteOnly", "Invite-only") },
                  ]}
                  value={tier.kind}
                  onChange={(v) => setKind(v as TicketKind)}
                  disabled={!canWrite}
                  aria-label={t("tickets.groupEditorPage.type", "Type")}
                  style={{ gap: 0, width: "100%" }}
                />
              </TgField>
              <TgField label={t("tickets.groupEditorPage.priceUsd", "Price (USD)")}>
                <input type="number" min={0} step={1} value={tier.price} disabled={!canWrite || tier.kind === "free" || tier.kind === "invite"} onChange={(e) => onChange({ price: parseFloat(e.target.value || "0") })} style={tgInput({ opacity: (tier.kind === "free" || tier.kind === "invite") ? 0.5 : 1 })} />
              </TgField>
            </div>
            <TgField label={t("tickets.groupEditorPage.description", "Description")} hint={t('tickets.groupEditorPage.hint2')}><input value={tier.desc} disabled={!canWrite} onChange={(e) => onChange({ desc: e.target.value })} placeholder={t("tickets.groupEditorPage.whatSIncludedWithThisTier", "What's included with this tier?")} style={tgInput()} /></TgField>
            <TgField label={t("tickets.groupEditorPage.colour", "Colour")}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {TG_PALETTE.map((c) => (
                  <button key={c} type="button" disabled={!canWrite} onClick={() => onChange({ color: c })} aria-label={i18nT("tickets.groupEditorPage.useColourC.icu", { value0: c })} style={{ width: 26, height: 26, borderRadius: 7, background: c, cursor: canWrite ? "pointer" : "default", border: tier.color === c ? `2px solid ${T.n1}` : "2px solid transparent", boxShadow: tier.color === c ? `0 0 0 2px ${T.white} inset` : "none" }} />
                ))}
              </div>
            </TgField>
          </>)}
          <CommsFlowEditor tier={tier} canWrite={canWrite} onChange={(flow) => onChange({ flow })} />
        </div>
      ) : null}
    </Card>
  );
}

function CommsFlowEditor({ tier, onChange, canWrite }: { tier: TicketTier; onChange: (flow: FlowStep[]) => void; canWrite: boolean }) {
  const t = useT();
  const flow = tier.flow || [];
  const setStep = (id: string, patch: Partial<FlowStep>) => onChange(flow.map((s) => s.id === id ? { ...s, ...patch } : s));
  const removeStep = (id: string) => onChange(flow.filter((s) => s.id !== id));
  const addStep = (type: FlowStepType) => onChange([...flow, tgStep(type)]);
  const present = new Set(flow.map((s) => s.type));
  const addable = (Object.keys(TG_STEP_TYPES) as FlowStepType[]).filter((t) => (t === "reminder" || t === "thankyou") ? true : !present.has(t));

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8 }}>
      <div style={{ padding: "12px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 26, height: 26, borderRadius: 7, background: alpha(tier.color, "22"), color: tier.color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.send size={13} /></span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t("tickets.groupEditorPage.communicationsFlow", "Communications flow")}</div>
          <div style={{ fontSize: 11, color: T.soft }}>{t("tickets.groupEditorPage.messagesSentToGuestsOnThisTierToggleRetimeOrAddSteps", "Messages sent to guests on this tier. Toggle, retime, or add steps.")}</div>
        </div>
      </div>
      <div style={{ padding: "8px 14px 14px" }}>
        {flow.map((step, i) => <FlowStepRow key={step.id} step={step} color={tier.color} last={i === flow.length - 1} canWrite={canWrite} onChange={(patch) => setStep(step.id, patch)} onRemove={() => removeStep(step.id)} />)}
        {flow.length === 0 ? <div style={{ padding: "12px 0", fontSize: 12, color: T.soft }}>{t("tickets.groupEditorPage.noMessagesGuestsOnThisTierWonTReceiveAutomatedComms", "No messages — guests on this tier won't receive automated comms.")}</div> : null}
        {canWrite && addable.length ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, paddingTop: 12, borderTop: `1px dashed ${T.border}` }}>
            <span style={{ fontSize: 12, color: T.soft, alignSelf: "center", marginRight: 2 }}>{t("tickets.groupEditorPage.addStep", "Add step:")}</span>
            {addable.map((t) => {
              const meta = TG_STEP_TYPES[t]; const I = iconByName(meta.icon);
              return <button key={t} type="button" onClick={() => addStep(t)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999, border: `1px solid ${T.n4}`, background: T.white, color: T.n1, fontFamily: T.font, fontSize: 12, fontWeight: 600, cursor: "pointer" }}><I size={12} /> {meta.label}</button>;
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FlowStepRow({ step, color, last, onChange, onRemove, canWrite }: { step: FlowStep; color: string; last: boolean; onChange: (patch: Partial<FlowStep>) => void; onRemove: () => void; canWrite: boolean }) {
  const t = useT();
  const meta = TG_STEP_TYPES[step.type] || { label: step.type, icon: "mail", blurb: "" };
  const I = iconByName(meta.icon);
  const dim = !step.on;
  return (
    <div style={{ display: "flex", gap: 12, position: "relative" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <span style={{ width: 30, height: 30, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center", background: step.on ? color : T.n5, color: step.on ? T.white : T.soft, transition: "background 120ms" }}><I size={14} /></span>
        {!last ? <span style={{ width: 2, flex: 1, minHeight: 14, background: T.border }} /> : null}
      </div>
      <div style={{ flex: 1, paddingBottom: last ? 0 : 14, opacity: dim ? 0.55 : 1, transition: "opacity 120ms" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{meta.label}</span>
          <span style={{ flex: 1 }} />
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: canWrite ? "pointer" : "default" }}>
            <Toggle on={step.on} onChange={canWrite ? ((v) => onChange({ on: v })) : undefined} />
          </label>
          {canWrite ? <IconBtn size={28} label={t("tickets.groupEditorPage.removeStep", "Remove step")} onClick={onRemove}><Icon.x size={12} /></IconBtn> : null}
        </div>
        {step.on ? (
          <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "auto 1fr", gap: 8, alignItems: "center" }}>
            <div style={{ display: "inline-flex", border: `1px solid ${T.n4}`, borderRadius: 6, overflow: "hidden" }}>
              {([["email", "Email", "mail"], ["sms", "SMS", "phone"]] as const).map(([v, l, ic]) => {
                const CI = iconByName(ic); const active = step.channel === v;
                return <button key={v} type="button" disabled={!canWrite} onClick={() => onChange({ channel: v })} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 10px", border: "none", background: active ? T.p1 : T.white, color: active ? T.white : T.n2, fontFamily: T.font, fontSize: 12, fontWeight: 600, cursor: canWrite ? "pointer" : "default", borderRight: v === "email" ? `1px solid ${T.n4}` : "none" }}><CI size={12} /> {l}</button>;
              })}
            </div>
            <Dropdown
              options={TG_TIMINGS.map((v) => ({ value: v, label: v }))}
              value={step.timing}
              onChange={(v) => onChange({ timing: v })}
              disabled={!canWrite}
              aria-label={t("tickets.groupEditorPage.timing", "Timing")}
              style={{ gap: 0, width: "100%" }}
            />
            <div style={{ gridColumn: "1 / -1" }}>
              <input value={step.subject} disabled={!canWrite} onChange={(e) => onChange({ subject: e.target.value })} placeholder={i18nT("tickets.groupEditorPage.metaLabelSubjectMetaBlurb.icu", { value0: meta.label, value1: meta.blurb })} style={tgInput({ height: 34, fontSize: 13 })} />
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{meta.blurb} {t("tickets.groupEditorPage.off", "· off")}</div>
        )}
      </div>
    </div>
  );
}
